// Where the booking page's chat (ConciergeWidget) keeps its place, per salon,
// in sessionStorage:
//
//   beuConciergeSession_<salonId>        the session id
//   beuConciergeSessionToken_<salonId>   its token: the secret the API needs to
//                                        continue the chat or read it back (the
//                                        id alone is guessable, so it isn't one)
//   beuConciergeSessionOwner_<salonId>   who was signed in when it was last used
//
// Every key starts with CONCIERGE_SESSION_PREFIX, so logging out can clear them
// all (clearAllConciergeSessions) without knowing which salons were visited.
//
// A signed-in client's chat is never carried over to anyone else. The API
// enforces that (a different login, or none, gets a new session); this is the
// same rule on the device, for logout paths that don't clear it: a session
// owned by a signed-in client is dropped as soon as someone else, or nobody,
// is signed in. A guest's chat carries on when they log in, as the API does.

export const CONCIERGE_SESSION_PREFIX = 'beuConciergeSession';

const idKey = (salonId) => `${CONCIERGE_SESSION_PREFIX}_${salonId}`;
const tokenKey = (salonId) => `${CONCIERGE_SESSION_PREFIX}Token_${salonId}`;
const ownerKey = (salonId) => `${CONCIERGE_SESSION_PREFIX}Owner_${salonId}`;

/** Who is signed in on this device right now: their user id, or '' for a guest. */
export function currentConciergeOwner() {
    try {
        if (!localStorage.getItem('accessToken')) return '';
        return localStorage.getItem('userId') || 'signed-in';
    } catch (e) {
        return '';
    }
}

export function clearConciergeSession(salonId) {
    try {
        sessionStorage.removeItem(idKey(salonId));
        sessionStorage.removeItem(tokenKey(salonId));
        sessionStorage.removeItem(ownerKey(salonId));
    } catch (e) { /* storage blocked: nothing kept */ }
}

/**
 * This salon's chat, as { sessionId, sessionToken }, or null when there is
 * none or it belonged to someone who is no longer the one signed in.
 */
export function readConciergeSession(salonId) {
    try {
        const sessionId = sessionStorage.getItem(idKey(salonId));
        if (!sessionId) return null;
        const owner = sessionStorage.getItem(ownerKey(salonId)) || '';
        if (owner && owner !== currentConciergeOwner()) {
            clearConciergeSession(salonId);
            return null;
        }
        return { sessionId, sessionToken: sessionStorage.getItem(tokenKey(salonId)) || '' };
    } catch (e) {
        return null;
    }
}

/**
 * Keep the session a reply came back with. The token only arrives when a
 * session starts; a new id without one means the old token is dead too.
 */
export function saveConciergeSession(salonId, { sessionId, sessionToken }) {
    if (!sessionId) return;
    try {
        const prev = sessionStorage.getItem(idKey(salonId));
        sessionStorage.setItem(idKey(salonId), sessionId);
        if (sessionToken) sessionStorage.setItem(tokenKey(salonId), sessionToken);
        else if (prev !== sessionId) sessionStorage.removeItem(tokenKey(salonId));
        sessionStorage.setItem(ownerKey(salonId), currentConciergeOwner());
    } catch (e) { /* storage blocked: the chat still works, it just won't survive a refresh */ }
}

/** Forget every salon's chat on this device. Called on logout. */
export function clearAllConciergeSessions() {
    try {
        const keys = [];
        for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i);
            if (k && k.indexOf(CONCIERGE_SESSION_PREFIX) === 0) keys.push(k);
        }
        keys.forEach((k) => sessionStorage.removeItem(k));
    } catch (e) { /* storage blocked: nothing kept */ }
}

// A random id for this browser, kept in localStorage, that the chat sends with
// every message (concierge hardening 2026-10). The API only ever uses it to ADD
// a limit (messages and new chats per browser), never to identify anyone, so a
// cleared or blocked storage just means a fresh id. Shape is the API's own:
// 16-64 of [A-Za-z0-9_-].
export const CONCIERGE_DEVICE_KEY = 'beuConciergeDeviceId';
const DEVICE_ID_RE = /^[A-Za-z0-9_-]{16,64}$/;
let memoryDeviceId = null;

function newDeviceId() {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let out = '';
    try {
        const bytes = new Uint8Array(32);
        (window.crypto || window.msCrypto).getRandomValues(bytes);
        bytes.forEach((b) => { out += alphabet[b % alphabet.length]; });
    } catch (e) {
        for (let i = 0; i < 32; i++) out += alphabet[Math.floor(Math.random() * alphabet.length)];
    }
    return out;
}

/** This browser's chat device id: kept when storage allows, else for this page load. */
export function conciergeDeviceId() {
    try {
        const kept = localStorage.getItem(CONCIERGE_DEVICE_KEY);
        if (kept && DEVICE_ID_RE.test(kept)) return kept;
        const fresh = newDeviceId();
        localStorage.setItem(CONCIERGE_DEVICE_KEY, fresh);
        return fresh;
    } catch (e) {
        if (!memoryDeviceId) memoryDeviceId = newDeviceId();
        return memoryDeviceId;
    }
}
